<template>
  <div class="referral-view-container">
    <div v-if="loading" class="text-center">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>

    <v-card v-else-if="!active" class="mx-auto text-center" elevation="4" rounded="lg">
      <div style="max-width: 500px">
        <div class="form-header">
          <v-card-title class="form-header__title">
            <v-img class="form-header__logo" contain src="https://app.wellcomefit.ch/img/logos/socialMedia/logo.png"></v-img>
            BRING A FRIEND
          </v-card-title>
        </div>
        <div class="pa-8">
          <v-icon size="60" color="info">mdi-information-outline</v-icon>
          <h3 class="text-h5 font-weight-bold mt-4">Funktion nicht aktiv</h3>
          <p class="body-1 mt-2">Das "Bring a Friend"-Programm ist für dein Studio aktuell nicht verfügbar.</p>
          <v-btn class="mt-5" color="primary" large block rounded @click="returnToHome">Zurück zur Startseite</v-btn>
        </div>
      </div>
    </v-card>

    <v-card v-else class="mx-auto" elevation="4" rounded="lg">
      <div style="max-width: 500px">
        <div class="form-header">
          <v-card-title class="form-header__title">
            <v-img class="form-header__logo" contain src="https://app.wellcomefit.ch/img/logos/socialMedia/logo.png"></v-img>
            BRING A FRIEND
          </v-card-title>
        </div>

        <v-img v-if="leadSetting.internPicture" :src="'/img' + leadSetting.internPicture"></v-img>

        <div v-if="!conditionsAccepted" class="pa-6">
          <h4 class="primary--text" style="font-weight: 1000; font-style: italic; text-transform: uppercase">Beschreibung</h4>
          <div class="html-content" v-html="leadSetting.description"></div>

          <h4 class="primary--text" style="font-weight: 1000; font-style: italic; text-transform: uppercase">Bedingungen</h4>
          <div class="html-content" v-html="leadSetting.internConditions"></div>

          <v-divider class="my-6"></v-divider>

          <p class="text-center body-1">Bedingungen akzeptieren und deinen persönlichen Empfehlungs-Link erhalten:</p>
          <v-card-actions class="mt-4 d-flex flex-column">
            <v-btn color="primary" x-large rounded @click="conditionsAccepted = true"> Akzeptieren & Link erhalten </v-btn>
            <v-btn class="mt-5" text large @click="returnToHome">Zurück</v-btn>
          </v-card-actions>
        </div>

        <div v-else class="pa-6">
          <h5 class="text-center" style="font-weight: 1000; font-style: italic; text-transform: uppercase">Dein persönlicher Link</h5>
          <p class="text-center body-2 mb-4">Teile diesen Link mit deinen Freunden. Für jede erfolgreiche Empfehlung erhältst du eine Belohnung!</p>
          <v-text-field
            :value="referralLink"
            outlined
            readonly
            label="Dein Empfehlungs-Link"
            prepend-inner-icon="mdi-link-variant"
            append-icon="mdi-content-copy"
            @click:append="copyLink">
            <template v-slot:append>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" @click="copyLink">mdi-content-copy</v-icon>
                </template>
                <span>Link kopieren</span>
              </v-tooltip>
            </template>
          </v-text-field>

          <div class="d-flex justify-center">
            <v-btn @click="copyLink" class="mx-auto" color="primary" x-large> <v-icon>mdi-content-copy</v-icon> Link kopieren</v-btn>
          </div>

          <p class="text-center font-weight-medium mt-6">oder direkt teilen über:</p>
          <div class="d-flex justify-center my-4">
            <v-btn icon large class="mx-2 share-btn" @click="shareLink('whatsapp')"><v-icon color="#25D366">mdi-whatsapp</v-icon></v-btn>
            <v-btn icon large class="mx-2 share-btn" @click="shareLink('facebook')"><v-icon color="#1877F2">mdi-facebook</v-icon></v-btn>
            <v-btn icon large class="mx-2 share-btn" @click="shareLink('telegram')"><v-icon color="#2AABEE">mdi-send</v-icon></v-btn>
            <v-btn icon large class="mx-2 share-btn" @click="shareLink('email')"><v-icon color="#B23121">mdi-email-outline</v-icon></v-btn>
            <v-btn v-if="canUseWebShare" icon large class="mx-2 share-btn" @click="shareViaWebShare"><v-icon>mdi-share-variant</v-icon></v-btn>
          </div>

          <v-divider class="my-6"></v-divider>

          <v-btn color="primary" x-large block rounded @click="returnToHome"> Zurück zur Startseite </v-btn>
        </div>

        <div class="form-footer"></div>
      </div>
    </v-card>
  </div>
</template>

<script>
import api from "../../../api"
import { mapActions } from "vuex"

export default {
  data() {
    return {
      loading: true,
      active: true,
      conditionsAccepted: false,
      leadSetting: null,
      user: {},
      host: null,
    }
  },
  async created() {
    this.host = window.location.host.replace("www.", "")
    this.setNavigationbarVisibility(false)
    try {
      const userData = await api.getUser()
      if (userData && userData.user) this.user = userData.user
      await this.initReferral()
    } catch (e) {
      this.$toast.error("Benutzerdaten konnten nicht geladen werden.")
      this.active = false
      this.loading = false
    }
  },
  beforeDestroy() {
    this.setNavigationbarVisibility(true)
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationbarVisibility"]),

    async initReferral() {
      try {
        const studioData = await api.getUserStudio()
        if (studioData.studio && studioData.studio.leadSetting && studioData.studio.leadSetting.active) {
          this.leadSetting = studioData.studio.leadSetting
        } else {
          this.active = false
        }
      } catch (e) {
        this.active = false
        console.error("Weiterempfehlung nicht aktiviert oder Fehler:", e)
      } finally {
        this.loading = false
      }
    },

    returnToHome() {
      this.$router.push("/")
    },

    copyLink() {
      navigator.clipboard.writeText(this.referralLink)
      this.$toast.success("Link in Zwischenablage kopiert")
    },

    // Zentrale Methode zum Teilen auf verschiedenen Plattformen
    shareLink(platform) {
      const text = encodeURIComponent(this.leadSetting.referralText || "Schau dir das an!")
      const url = encodeURIComponent(this.referralLink)
      let shareUrl = ""

      switch (platform) {
        case "whatsapp":
          shareUrl = `https://api.whatsapp.com/send?text=${text}%20${url}`
          break
        case "facebook":
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`
          break
        case "telegram":
          shareUrl = `https://t.me/share/url?url=${url}&text=${text}`
          break
        case "email":
          shareUrl = `mailto:?subject=Empfehlung&body=${text}%0A%0A${this.referralLink}`
          break
      }

      if (shareUrl) {
        window.open(shareUrl, "_blank", "noopener,noreferrer")
      }
    },

    // Fallback für mobile Geräte
    shareViaWebShare() {
      if (this.canUseWebShare) {
        navigator.share({
          title: "Bring a Friend",
          text: this.leadSetting.referralText,
          url: this.referralLink,
        })
      }
    },
  },
  computed: {
    referralLink() {
      return this.user.email && this.host ? `https://${this.host}/r/${this.user.email.toLowerCase()}` : ""
    },
    canUseWebShare() {
      return !!navigator.share
    },
  },
}
</script>

<style lang="scss" scoped>
.referral-view-container {
  background-color: #f4f6f8;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

// Gemeinsamer Stil für Header, wiederverwendet vom Formular
.form-header {
  background-color: #212121;
  border-bottom: 7px solid var(--v-primary);
  padding: 1rem;

  &__title {
    font-weight: 1000 !important;
    font-style: italic;
    color: white;
    justify-content: center;
    align-items: center;
    text-align: center;
    font-size: 1.5rem; // Angepasste Grösse
  }

  &__logo {
    height: 80px;
    width: 80px;
    max-width: 80px;
    margin-right: 1rem;
  }
}

// Stil für v-html Inhalte
.html-content {
  line-height: 1.6;

  ::v-deep p {
    margin-bottom: 1rem;
  }
  ::v-deep ul {
    padding-left: 20px;
  }
}

.share-btn {
  border: 1px solid #e0e0e0;
}
</style>
